import api from "./api";

export interface EditalTemplate {
  id: number;
  nome: string;
  descricao: string;
  versao: number;
  ativo: boolean;
  total_editais?: number;
  schema_campos?: Record<
    string,
    {
      type:
        | "text"
        | "textarea"
        | "number"
        | "select"
        | "currency"
        | "date"
        | "url";
      label: string;
      required?: boolean;
      placeholder?: string;
      maxLength?: number;
      min?: number;
      max?: number;
      default?: string | number;
      options?: string[];
    }
  >;
  configuracoes_padrao?: Record<string, unknown>;
  criado_por?: {
    id: number;
    nome: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface AnexoEdital {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  url: string;
  created_at: string;
  adicionado_por?: {
    id: number;
    nome: string;
    email: string;
  };
  adicionado_em?: string;
}

export interface Edital {
  id: number;
  nome: string;
  descricao: string;
  data_inicio: string;
  data_fim: string;
  status: "rascunho" | "publicado" | "encerrado";
  edital_pdf_url?: string; // Mantém compatibilidade
  anexos?: AnexoEdital[]; // Nova lista de anexos
  edital_template_id: number;
  formacao_minima_requerida?: string; // graduacao, especializacao, mestrado, doutorado, pos_doutorado
  configuracoes?: Record<string, unknown>;
  criado_por_id: number;
  created_at: string;
  updated_at: string;
}

export interface EditalDetalhado extends Edital {
  template: EditalTemplate;
  criado_por: {
    id: number;
    nome: string;
  };
  estatisticas?: {
    total_inscricoes: number;
    pendentes: number;
    em_analise: number;
    aprovadas: number;
    reprovadas: number;
  };
}

export interface CriarEditalParams {
  nome: string;
  descricao: string;
  data_inicio: string;
  data_fim: string;
  edital_template_id: number;
  formacao_minima_requerida?: string;
  anexos?: File[]; // Mudou para array de arquivos
  configuracoes?: Record<string, unknown>;
}

const editaisAdminService = {
  // Listar templates ativos
  listarTemplates: async (): Promise<{ templates: EditalTemplate[] }> => {
    const response = await api.get("/api/v1/admin/edital_templates", {
      params: { ativo: true },
    });
    return response.data;
  },

  // Buscar template com detalhes
  buscarTemplate: async (id: number): Promise<{ template: EditalTemplate }> => {
    const response = await api.get(`/api/v1/admin/edital_templates/${id}`);
    return response.data;
  },

  // Listar editais
  listar: async (params?: {
    status?: string;
    pagina?: number;
  }): Promise<{ editais: Edital[] }> => {
    const response = await api.get("/api/v1/admin/editais", { params });
    return response.data;
  },

  // Buscar edital
  buscar: async (id: number): Promise<{ edital: EditalDetalhado }> => {
    const response = await api.get(`/api/v1/admin/editais/${id}`);
    return response.data;
  },

  // Criar edital
  criar: async (params: CriarEditalParams): Promise<{ edital: Edital }> => {
    const formData = new FormData();
    formData.append("edital[nome]", params.nome);
    formData.append("edital[descricao]", params.descricao);
    formData.append("edital[data_inicio]", params.data_inicio);
    formData.append("edital[data_fim]", params.data_fim);
    formData.append(
      "edital[edital_template_id]",
      params.edital_template_id.toString()
    );
    if (params.formacao_minima_requerida) {
      formData.append("edital[formacao_minima_requerida]", params.formacao_minima_requerida);
    }

    // CORREÇÃO: Backend espera anexos[] diretamente, não edital[anexos][]
    if (params.anexos && params.anexos.length > 0) {
      params.anexos.forEach((arquivo) => {
        formData.append("anexos[]", arquivo);
      });
    }

    if (params.configuracoes) {
      formData.append(
        "edital[configuracoes]",
        JSON.stringify(params.configuracoes)
      );
    }

    const response = await api.post("/api/v1/admin/editais", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Atualizar edital
  atualizar: async (
    id: number,
    params: Partial<CriarEditalParams>
  ): Promise<{ edital: Edital }> => {
    const formData = new FormData();

    if (params.nome) formData.append("edital[nome]", params.nome);
    if (params.descricao)
      formData.append("edital[descricao]", params.descricao);
    if (params.data_inicio)
      formData.append("edital[data_inicio]", params.data_inicio);
    if (params.data_fim) formData.append("edital[data_fim]", params.data_fim);
    if (params.formacao_minima_requerida !== undefined) {
      formData.append("edital[formacao_minima_requerida]", params.formacao_minima_requerida);
    }
    
    // CORREÇÃO: Backend espera anexos[] diretamente, não edital[anexos][]
    if (params.anexos && params.anexos.length > 0) {
      params.anexos.forEach((arquivo) => {
        formData.append("anexos[]", arquivo);
      });
    }
    
    if (params.configuracoes)
      formData.append(
        "edital[configuracoes]",
        JSON.stringify(params.configuracoes)
      );

    const response = await api.put(`/api/v1/admin/editais/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Publicar edital
  publicar: async (
    id: number
  ): Promise<{ message: string; edital: Edital }> => {
    const response = await api.post(`/api/v1/admin/editais/${id}/publicar`);
    return response.data;
  },

  // Encerrar edital
  encerrar: async (
    id: number
  ): Promise<{ message: string; edital: Edital }> => {
    const response = await api.post(`/api/v1/admin/editais/${id}/encerrar`);
    return response.data;
  },

  // Excluir edital
  excluir: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/v1/admin/editais/${id}`);
    return response.data;
  },

  // Estatísticas do edital
  estatisticas: async (
    id: number
  ): Promise<{ estatisticas: EditalDetalhado["estatisticas"] }> => {
    const response = await api.get(`/api/v1/admin/editais/${id}/estatisticas`);
    return response.data;
  },

  // Adicionar anexos ao edital
  anexar: async (
    id: number,
    anexos: File[]
  ): Promise<{ message: string; edital: EditalDetalhado }> => {
    const formData = new FormData();
    anexos.forEach((arquivo) => {
      formData.append("anexos[]", arquivo);
    });

    const response = await api.post(
      `/api/v1/admin/editais/${id}/anexar`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Remover anexo do edital
  removerAnexo: async (
    id: number,
    anexoId: number
  ): Promise<{ message: string; edital: EditalDetalhado }> => {
    const response = await api.delete(
      `/api/v1/admin/editais/${id}/anexo/${anexoId}`
    );
    return response.data;
  },
};

export default editaisAdminService;
